import { type ContentfulClientApi } from 'contentful';
import pLimit from 'p-limit';

import {
  type RecurseParentsContext,
  type RecurseParentsResult,
  recurseParents,
} from './recurseParents';

type FindParentsOfEntryOptions = {
  /**
   * The ID of the entry to find parents of
   */
  id: string;
  /**
   * The target content type to search for. The crawler will stop recursing through entry's parents when it finds an entry of this type.
   */
  contentType: string;
  /**
   * The Contentful client to use for querying
   */
  client: ContentfulClientApi;
  /**
   * The maximum amount of time to spend recursing through parents. If the crawler has not finished after this time, it will throw an error.
   * @default 15
   */
  timeout?: number;
  /**
   * The maximum number of concurrent queries to Contentful. This is used to prevent Contentful from throttling requests.
   * @default 7
   * @see https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/api-rate-limits
   */
  queryConcurrency?: number;
};

type FindParentsOfEntryResult<T> = RecurseParentsResult<T> & {
  /**
   * The total amount of time spent recursing through parents in milliseconds.
   */
  runtime: number;
  /**
   * The IDs of all entries that were crawled while searching for parent entries
   */
  crawledIds: string[];
};

export async function findParentsOfEntry<T = unknown>({
  id,
  contentType,
  client,
  timeout = 15,
  queryConcurrency = 5,
}: FindParentsOfEntryOptions): Promise<FindParentsOfEntryResult<T>> {
  const start = process.hrtime.bigint();
  const deadline = start + BigInt(timeout * 1000000000);
  const context: RecurseParentsContext<T> = {
    originalId: id,
    crawledIds: new Map(),
    limit: pLimit(queryConcurrency),
    contentType,
    timeout,
    deadline,
  };

  const result = await recurseParents<T>(id, client, context);

  return {
    ...result,
    runtime: Number(process.hrtime.bigint() - start) / 1000000,
    crawledIds: Array.from(context.crawledIds.keys()),
  };
}
